import { Helmet } from 'react-helmet-async';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { bootstrapApp } from './bootstrap';

import SignIn from 'containers/SignIn/Loadable';
import SignOut from 'containers/SignOut';
import AccessDeniedAdmin from 'containers/AccessDeniedAdmin/Loadable';

import LoggedOutPage from 'components/LoggedOutPage';
import NotFound from 'components/NotFound';
import WithoutStore from 'components/WithoutStore';
import { ScrollToTop } from 'components/ScrollToTop';

import { useAuth } from 'hooks/useAuth';
import useCommandBar from 'hooks/useCommandBar';
import useOrganization from 'hooks/Organization/useOrganization';

import { MENU_ROUTES } from 'utils/menuMaker/constants';

import AppRoute, { PrivateRoute, PrivateRouteNoFrame } from './PrivateRoute';
import PublicRoute from './PublicRoute';
import { HomeRedirect } from './HomeRedirect';
import { InventoriesEntry } from './AppRoutesModules/InventoriesEntry';
import { InvoicingRoutes } from './AppRoutesModules/InvoicingRoutes';
import { ReportsRoutes } from './AppRoutesModules/ReportsRoutes';
import { OnlineOrderingRoutes } from './AppRoutesModules/OnlineOrderingRoutes';
import { SettingsRoutes } from './AppRoutesModules/SettingsRoutes';
import { WorkforceRoutes } from './AppRoutesModules/WorkforceRoutes';
import { EmployeesRoutes } from './AppRoutesModules/EmployeesRoutes';
import { RolesRoutes } from './AppRoutesModules/RolesRoutes';
import { MenuMakerRoutes } from './AppRoutesModules/MenuMakerRoutes';
import { SubscriptionRoutes } from './AppRoutesModules/SubscriptionRoutes';
import { ReferralRoutes } from './AppRoutesModules/ReferralRoutes';
import { LegalRoutes } from './AppRoutesModules/LegalRoutes';
import { AuthRoutes } from './AppRoutesModules/AuthRoutes';
import { OnboardingWizardPage } from '@onboarding/presentation/pages';
import {
  AccountVerificationPage,
  ChangePasswordPage,
  PhoneVerificationPage,
  ProfilePage,
} from '@identity/presentation/pages';

function App() {
  const auth = useAuth();
  bootstrapApp(auth);
  const { isAuthenticated, user, store } = auth;
  const isDraftStore = store?.isDraft ?? false;

  useCommandBar();
  const { i18n } = useTranslation();
  const isInternal = /@parrotsoftware.io/.test(user?.email || '') ? ';internal' : '';
  const location = useLocation();
  const { organization } = useOrganization();
  const usersV3 = organization?.internalPermissionsSystem || false;

  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    if (window._cio) {
      // eslint-disable-next-line
      _cio.page(`${window.location.origin}${location.pathname}`);
    }
  }, [location]);

  return (
    <>
      <ScrollToTop />
      <Helmet defaultTitle="ParrotConnect" htmlAttributes={{ lang: i18n.language }} titleTemplate="%s - ParrotConnect">
        <meta content="ParrotConnect" name="ParrotConnect" />
        {isAuthenticated && (
          <>
            {user?.email && store?.organizationUuid ? (
              <script type="text/javascript">
                {`
                  _cio.identify({
                    id: '${store?.uuid}',
                    name: '${store?.name}',
                    email: '${user?.email}',
                    organization: '${store?.organizationUuid}',
                    isInternal: '${!!isInternal}',
                    });
                  `}
              </script>
            ) : null}
            {user?.email && user?.uuid && store?.uuid ? (
              <script>
                {`
                  try {
                    Canny(
                      'identify',
                      {
                        appID: '6698575a7db4ab6da5905a38',
                        user: {
                          email: '${user.email}',
                          id: '${user.uuid}',
                          name: '${user.firstName || user.email} ${user.lastName || ''}',
                          customFields: {
                            storeUuid: '${store.uuid}',
                          },
                        },
                    });
                  } catch (error) {
                    if (window.apm && typeof window.apm.captureError === 'function') {
                      window.apm.captureError(error);
                    }
                  }
                `}
              </script>
            ) : null}
          </>
        )}
      </Helmet>

      <Switch>
        <Route component={HomeRedirect} exact path="/" />

        {/* Legacy redirect: workforce root → workforce/roles (only for legacy version) */}
        {!usersV3 && <Redirect exact from="/workforce" to="/workforce/roles" />}

        <Route component={WithoutStore} exact path="/without-store" />
        <PublicRoute component={SignIn} exact path="/signin" />
        <Route component={LoggedOutPage} exact path="/logged-out" />
        <Route component={SignOut} exact path="/signout" />
        <AppRoute component={AccessDeniedAdmin} exact path="/permissions-denied" />

        {/* Menu Maker Module */}
        <Route component={MenuMakerRoutes} path={MENU_ROUTES.ROOT} />
        <Route component={MenuMakerRoutes} path="/menus" />

        {/* Workforce/Employees Module - Conditional based on usersV3 flag */}
        {usersV3 && <Route component={EmployeesRoutes} path="/employees" />}
        {usersV3 && <Route component={RolesRoutes} path="/roles" />}
        {!usersV3 && <Route component={WorkforceRoutes} path="/workforce" />}

        {/* Invoicing Module */}
        <Route component={InvoicingRoutes} path="/invoicing" />

        {/* Settings Module */}
        <Route component={SettingsRoutes} path="/settings" />

        {/* Reports Module */}
        <Route component={ReportsRoutes} path="/reports" />

        {/* Inventories Module — Early demo, legacy or microfrontend, decided by plan and org rollout */}
        <Route component={InventoriesEntry} path="/inventories" />

        {/* Online Ordering Module */}
        <Route component={OnlineOrderingRoutes} path="/online-ordering" />

        {/* Subscription Module */}
        <Route component={SubscriptionRoutes} path={['/subscription', '/subscriptions']} />

        {/* Referral Module */}
        <Route component={ReferralRoutes} path="/referral" />

        {/* Onboarding Wizard — fullscreen, no sidebar. Disabled for draft stores. */}
        <PrivateRouteNoFrame accessRoute={!isDraftStore} component={OnboardingWizardPage} exact path="/onboarding" />

        {/* Identity — mandatory account verification. Private, fullscreen, no frame. */}
        <PrivateRouteNoFrame component={AccountVerificationPage} exact path="/verify-account" />

        {/* Identity — Mi perfil. Private, inside the frame; its two wizards are fullscreen. */}
        <PrivateRouteNoFrame component={PhoneVerificationPage} exact path="/profile/verify-phone" />
        <PrivateRouteNoFrame component={ChangePasswordPage} exact path="/profile/change-password" />
        <PrivateRoute component={ProfilePage} exact path="/profile" />

        {/* Legal Module - Public routes (no authentication required) */}
        <Route component={LegalRoutes} path="/legal" />

        {/* Identity — magic link landing + activation wizard. Public, fullscreen, no frame. */}
        <Route component={AuthRoutes} path="/auth" />

        {/* Not found page */}
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

export default App;
